import { Container, Graphics, type Ticker } from 'pixi.js';
import { DefaultRandom, type Random } from '../core/Random';

/**
 * Multi-rocket fireworks display. Each rocket launches from the bottom
 * of a target area, trails sparkles upward to an apex, then explodes
 * into a radial burst of glowing particles with gravity-driven streaks.
 *
 * <p>Producer brief 2026-06-04 — "Fireworks?" — escalating-tier
 * celebration polish; bigger wins should feel like an event, not just
 * a louder burst. Fireworks are visually distinct from
 * {@link WinParticles} (which radiates from a single point with no
 * launch phase) and from {@link CoinShower} (which falls straight
 * down). A fireworks volley fires from many ground points up to
 * varied apexes, creating the staggered rhythm of an actual display.
 *
 * <p>Per rocket:
 * <ul>
 *   <li>Launch — small Graphics streak ascends from spawn to apex
 *       over {@code launchMs}, with a fading trail (alpha fades from
 *       1 → 0.3 along the path). Trail is a single Graphics drawn
 *       every frame; not particle-individual to keep the count low.
 *   <li>Burst — at apex, spawn {@code burstCount} radial particles
 *       with the rocket's colour. Each particle is a small Graphics
 *       circle that flies outward, fades, and falls under gravity.
 * </ul>
 *
 * <p>Self-cleans: when all rockets have launched + all burst particles
 * have aged out, the per-frame handler unregisters from the ticker.
 */
export class Fireworks extends Container {
  private rockets: RocketState[] = [];
  private bursts: BurstParticle[] = [];
  private pending: PendingRocket[] = [];
  private tickHandler: ((tick: Ticker) => void) | null = null;
  private elapsedMs = 0;

  constructor(
    private readonly ticker: Ticker,
    private readonly random: Random = new DefaultRandom(),
  ) {
    super();
  }

  /**
   * Schedule a fireworks volley. {@code count} rockets launch over
   * {@code spreadMs} milliseconds, each from a random x in
   * [{@code areaLeft}, {@code areaRight}] (ground at {@code groundY})
   * to a random apex inside the upper portion of the area. Colours are
   * picked uniformly from {@code colors}.
   *
   * <p>Calling {@code show} while a previous volley is still running
   * appends new rockets to the schedule — the in-flight rockets keep
   * playing out.
   */
  show(options: FireworksOptions): void {
    const colors = options.colors ?? DEFAULT_COLORS;
    const count = options.count ?? 5;
    const spreadMs = options.spreadMs ?? 1800;
    for (let i = 0; i < count; i++) {
      // Stagger launches with a small random jitter so they don't fire
      // at perfectly regular intervals.
      const baseDelay = (i / count) * spreadMs;
      const jitter = (this.random.nextFloat() - 0.5) * (spreadMs / count);
      const delay = Math.max(0, baseDelay + jitter);
      const x = options.areaLeft
        + this.random.nextFloat() * (options.areaRight - options.areaLeft);
      // Apex in the upper third of the area, with horizontal drift so
      // the rocket arcs a little rather than going straight up.
      const apexX = x + (this.random.nextFloat() - 0.5) * 120;
      const apexY = options.apexTopY
        + this.random.nextFloat() * (options.apexBottomY - options.apexTopY);
      this.pending.push({
        delayMs: this.elapsedMs + delay,
        spawnX: x,
        spawnY: options.groundY,
        apexX,
        apexY,
        color: colors[this.random.nextIntBetween(0, colors.length)]!,
        launchMs: options.launchMs ?? 750,
        burstCount: options.burstCount ?? 36,
      });
    }

    if (!this.tickHandler) {
      this.tickHandler = (tick) => this.advance(tick.deltaMS);
      this.ticker.add(this.tickHandler);
    }
  }

  destroyEffect(): void {
    if (this.tickHandler) {
      this.ticker.remove(this.tickHandler);
      this.tickHandler = null;
    }
    for (const r of this.rockets) r.sprite.destroy();
    for (const b of this.bursts) b.sprite.destroy();
    this.rockets = [];
    this.bursts = [];
    this.pending = [];
  }

  private advance(deltaMs: number): void {
    this.elapsedMs += deltaMs;
    const seconds = deltaMs / 1000;

    // ---- Launch any rockets whose delay has elapsed. ----
    if (this.pending.length > 0) {
      const stillPending: PendingRocket[] = [];
      for (const p of this.pending) {
        if (this.elapsedMs >= p.delayMs) {
          this.launchRocket(p);
        } else {
          stillPending.push(p);
        }
      }
      this.pending = stillPending;
    }

    // ---- Tick active rockets — ease them along the launch path. ----
    const nextRockets: RocketState[] = [];
    for (const r of this.rockets) {
      r.elapsedMs += deltaMs;
      const t = Math.min(1, r.elapsedMs / r.launchMs);
      // Quartic ease-out so the rocket decelerates near the apex,
      // mimicking gravity slowing its rise. (Real rockets actually
      // accelerate, but ease-out reads better visually — the burst
      // happens at a comprehensible stationary point.)
      const eased = 1 - (1 - t) * (1 - t) * (1 - t) * (1 - t);
      const x = r.spawnX + (r.apexX - r.spawnX) * eased;
      const y = r.spawnY + (r.apexY - r.spawnY) * eased;
      r.sprite.clear();
      // Trail: short streak from a few frames ago to current position.
      // We don't keep history — just a vertical streak from y+18 to y,
      // tinted with the rocket's colour, fading to transparent.
      const trailLen = 20;
      const trailDx = (r.apexX - r.spawnX) / r.launchMs * deltaMs * 4; // direction hint
      r.sprite
        .moveTo(x - trailDx * 0.3, y + trailLen)
        .lineTo(x, y)
        .stroke({ color: r.color, alpha: 0.6, width: 2 });
      // A bright tip dot.
      r.sprite.circle(x, y, 2.5).fill({ color: r.color, alpha: 0.95 });

      if (t >= 1) {
        // Reached apex — fire the burst and retire the rocket.
        this.spawnBurst(r.apexX, r.apexY, r.color, r.burstCount);
        r.sprite.destroy();
      } else {
        nextRockets.push(r);
      }
    }
    this.rockets = nextRockets;

    // ---- Tick burst particles. ----
    const nextBursts: BurstParticle[] = [];
    for (const b of this.bursts) {
      b.ageMs += deltaMs;
      if (b.ageMs >= b.lifeMs) {
        b.sprite.destroy();
        continue;
      }
      b.vy += b.gravity * seconds;
      b.sprite.x += b.vx * seconds;
      b.sprite.y += b.vy * seconds;
      // Alpha curve: bright for first 60%, then fade.
      const age = b.ageMs / b.lifeMs;
      b.sprite.alpha = age < 0.6 ? 1 : 1 - (age - 0.6) / 0.4;
      // Tail-streak: stretch the particle along its velocity vector
      // by a tiny amount so it reads as a streak rather than a dot.
      // Scale.y derives from |v| but capped so the streak doesn't
      // become a comet at high speeds.
      const speedRatio = Math.min(1, Math.hypot(b.vx, b.vy) / 600);
      b.sprite.scale.set(1, 1 + speedRatio * 1.2);
      // Rotate the sprite to point along its velocity so the stretch
      // aligns with motion direction.
      b.sprite.rotation = Math.atan2(b.vy, b.vx) + Math.PI / 2;
      nextBursts.push(b);
    }
    this.bursts = nextBursts;

    // ---- Self-stop when nothing left. ----
    if (
      this.rockets.length === 0
      && this.bursts.length === 0
      && this.pending.length === 0
      && this.tickHandler
    ) {
      this.ticker.remove(this.tickHandler);
      this.tickHandler = null;
      this.elapsedMs = 0;
    }
  }

  private launchRocket(p: PendingRocket): void {
    const sprite = new Graphics();
    this.addChild(sprite);
    this.rockets.push({
      sprite,
      spawnX: p.spawnX,
      spawnY: p.spawnY,
      apexX: p.apexX,
      apexY: p.apexY,
      color: p.color,
      launchMs: p.launchMs,
      elapsedMs: 0,
      burstCount: p.burstCount,
    });
  }

  private spawnBurst(x: number, y: number, color: number, count: number): void {
    // Slightly varied palette — the rocket's base colour plus a few
    // adjacent tints so the burst has visual richness instead of one
    // flat hue.
    const palette = [color, lighten(color, 0.3), lighten(color, -0.15), 0xffffff];
    for (let i = 0; i < count; i++) {
      const angle = this.random.nextFloat() * Math.PI * 2;
      const speed = 220 + this.random.nextFloat() * 280;
      const tint = palette[this.random.nextIntBetween(0, palette.length)]!;
      const sprite = new Graphics();
      sprite.rect(-1.5, -3, 3, 6).fill({ color: tint });
      sprite.x = x;
      sprite.y = y;
      this.addChild(sprite);
      this.bursts.push({
        sprite,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        ageMs: 0,
        lifeMs: 1300 + this.random.nextFloat() * 600,
        gravity: 380,
      });
    }
  }
}

interface RocketState {
  sprite: Graphics;
  spawnX: number;
  spawnY: number;
  apexX: number;
  apexY: number;
  color: number;
  launchMs: number;
  elapsedMs: number;
  burstCount: number;
}

interface BurstParticle {
  sprite: Graphics;
  vx: number;
  vy: number;
  ageMs: number;
  lifeMs: number;
  gravity: number;
}

interface PendingRocket {
  delayMs: number;
  spawnX: number;
  spawnY: number;
  apexX: number;
  apexY: number;
  color: number;
  launchMs: number;
  burstCount: number;
}

export interface FireworksOptions {
  /** Number of rockets to launch in this volley. */
  count?: number;
  /** Wall-clock duration over which the rockets are scheduled. They
   *  stagger evenly across this window with a small random jitter so
   *  the volley doesn't read as a metronome. */
  spreadMs?: number;
  /** Per-rocket launch time (spawn to apex). Defaults to 750ms. */
  launchMs?: number;
  /** Burst particle count per rocket. Defaults to 36. */
  burstCount?: number;
  /** x range across which rockets spawn at the ground. */
  areaLeft: number;
  areaRight: number;
  /** Ground y — rockets spawn here and travel up to an apex in
   *  [apexTopY, apexBottomY]. */
  groundY: number;
  apexTopY: number;
  apexBottomY: number;
  /** Colour palette. Each rocket picks one colour uniformly; the
   *  burst at apex uses that colour plus light/dark variants and
   *  white sparkles. */
  colors?: number[];
}

const DEFAULT_COLORS = [
  0xff6bd6, // brand pink
  0xffd23b, // warm gold
  0x42e2f5, // cyan
  0xff8a3d, // orange
  0xb475ff, // violet
  0x9de54e, // lime
  0xff5a8a, // hot pink
];

/** Brighten or darken a hex colour by {@code amount} in [-1, 1]. */
function lighten(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const adj = (c: number) => {
    if (amount >= 0) return Math.round(c + (255 - c) * amount);
    return Math.round(c * (1 + amount));
  };
  return (adj(r) << 16) | (adj(g) << 8) | adj(b);
}
